import { useEffect, useState } from "react";
import { ArrowRight, Bookmark, CalendarCheck, Moon, Music, Sun, Users } from "lucide-react";
import favicon from "../assets/branding/artistnav-beta-icon.png";
import "./helloLandingBeta.css";
import { BETA_API } from "@/api/betaAccess";

/* Production-capable ArtistNav landing. Waitlist uses the launch API;
 * registration requires a separate invitation. Illustrations are fictional. */

// Relative on purpose: resolves against whichever host ends up serving the app.
const SIGN_IN_PATH = import.meta.env.DEV ? "/login" : "https://app.artistnav.com/login";

const THEME_KEY = "hello-beta-dark-mode";

function useLandingDarkMode() {
  const [dark, setDark] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      const stored = localStorage.getItem(THEME_KEY);
      if (stored !== null) return stored === "true";
    } catch {
      // Storage unavailable — fall back to system preference.
    }
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
  });
  const toggle = () =>
    setDark((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(THEME_KEY, String(next));
      } catch {
        // Ignore storage errors.
      }
      return next;
    });
  return [dark, toggle] as const;
}

/* ------------------------------------------------------------------ */
/*  Network illustration                                               */
/* ------------------------------------------------------------------ */

type NetworkLayout = {
  width: number;
  artists: { id: string; name: string; x: number }[];
  venues: { id: string; name: string; x: number; playedBy: string[] }[];
};

// Fictional artists and venues. Two layouts so labels stay legible on phones.
const WIDE_LAYOUT: NetworkLayout = {
  width: 600,
  artists: [
    { id: "a1", name: "Mae Harlan", x: 100 },
    { id: "a2", name: "Lowland Pines", x: 300 },
    { id: "a3", name: "Two Rivers", x: 500 },
  ],
  venues: [
    { id: "v1", name: "Copperline Hall", x: 60, playedBy: ["a1", "a2"] },
    { id: "v2", name: "The Grange Room", x: 180, playedBy: ["a1", "a2", "a3"] },
    { id: "v3", name: "Millrace Hall", x: 300, playedBy: ["a2", "a3"] },
    { id: "v4", name: "Fern & Fiddle", x: 420, playedBy: ["a3"] },
    { id: "v5", name: "Union Depot", x: 540, playedBy: ["a1", "a3"] },
  ],
};

const COMPACT_LAYOUT: NetworkLayout = {
  width: 360,
  artists: [
    { id: "a1", name: "Mae Harlan", x: 60 },
    { id: "a2", name: "Lowland Pines", x: 180 },
    { id: "a3", name: "Two Rivers", x: 300 },
  ],
  venues: [
    { id: "v1", name: "Copperline Hall", x: 60, playedBy: ["a1", "a2"] },
    { id: "v2", name: "Grange Room", x: 180, playedBy: ["a1", "a2", "a3"] },
    { id: "v4", name: "Fern & Fiddle", x: 300, playedBy: ["a3"] },
  ],
};

const ARTIST_Y = 52;
const VENUE_Y = 196;
const YOU_Y = 330;

const NETWORK_LABEL =
  "Example: three comparable artists connect to the venues they have played. Venues played by several of them are stronger leads and feed into your next booking opportunities.";

function NetworkSvg({ layout, className }: { layout: NetworkLayout; className: string }) {
  const youX = layout.width / 2;
  return (
    <svg viewBox={`0 0 ${layout.width} 372`} role="img" aria-label={NETWORK_LABEL} className={className}>
      <g className="hb-net-edges" aria-hidden="true">
        {layout.venues.flatMap((venue) =>
          venue.playedBy.map((artistId) => {
            const artist = layout.artists.find((a) => a.id === artistId)!;
            return (
              <path
                key={`${artistId}-${venue.id}`}
                d={`M${artist.x},${ARTIST_Y + 16} C${artist.x},${ARTIST_Y + 80} ${venue.x},${VENUE_Y - 80} ${venue.x},${VENUE_Y - 12}`}
              />
            );
          }),
        )}
        {layout.venues
          .filter((v) => v.playedBy.length >= 2)
          .map((venue) => (
            <path
              key={`you-${venue.id}`}
              className="hb-net-edge--you"
              d={`M${venue.x},${VENUE_Y + 50} C${venue.x},${VENUE_Y + 100} ${youX},${YOU_Y - 80} ${youX},${YOU_Y - 20}`}
            />
          ))}
      </g>
      <g aria-hidden="true">
        {layout.artists.map((artist) => (
          <g key={artist.id} transform={`translate(${artist.x},${ARTIST_Y})`}>
            <circle r="15" className="hb-net-node hb-net-node--artist" />
            <text y="-24" textAnchor="middle" className="hb-net-label">
              {artist.name}
            </text>
          </g>
        ))}
        {layout.venues.map((venue) => (
          <g key={venue.id} transform={`translate(${venue.x},${VENUE_Y})`}>
            <rect
              x="-11"
              y="-11"
              width="22"
              height="22"
              rx="5"
              className={`hb-net-node hb-net-node--venue${venue.playedBy.length >= 2 ? " is-strong" : ""}`}
            />
            <text y="30" textAnchor="middle" className="hb-net-label hb-net-label--venue">
              {venue.name}
            </text>
            <text y="45" textAnchor="middle" className="hb-net-sublabel">
              Played by {venue.playedBy.length}
            </text>
          </g>
        ))}
        <g transform={`translate(${youX},${YOU_Y})`}>
          <rect x="-104" y="-18" width="208" height="36" rx="18" className="hb-net-you" />
          <text y="5" textAnchor="middle" className="hb-net-you-label">
            Your next opportunities
          </text>
        </g>
      </g>
    </svg>
  );
}

function NetworkDiagram() {
  return (
    <figure className="hb-network">
      <NetworkSvg layout={WIDE_LAYOUT} className="hb-net-wide" />
      <NetworkSvg layout={COMPACT_LAYOUT} className="hb-net-compact" />
      <figcaption className="hb-network-legend">
        <span><i className="hb-key hb-key--artist" aria-hidden="true" /> Artists like you</span>
        <span><i className="hb-key hb-key--venue" aria-hidden="true" /> Venues they&rsquo;ve played</span>
        <span><i className="hb-key hb-key--strong" aria-hidden="true" /> Played by several &mdash; worth a closer look</span>
        <span className="hb-network-note">Illustrative example</span>
      </figcaption>
    </figure>
  );
}

/* ------------------------------------------------------------------ */
/*  Workflow preview (mirrors Discover → Booking → Shows vocabulary)   */
/* ------------------------------------------------------------------ */

function WorkflowPreview() {
  return (
    <div className="hb-flow" aria-label="Example of the Discover, Booking and Shows workflow">
      <article className="hb-panel">
        <header className="hb-panel-head">
          <span className="hb-panel-tab">Discover</span>
          <span className="hb-panel-sub">Venues</span>
        </header>
        <div className="hb-venue-row">
          <div>
            <p className="hb-venue-name">The Grange Room</p>
            <p className="hb-venue-meta">Bloomington, IN</p>
            <p className="hb-venue-meta">
              Played by <strong>Mae Harlan</strong>, <strong>Lowland Pines</strong> +1
            </p>
          </div>
          <span className="hb-chip hb-chip--muted">Not in Booking</span>
        </div>
        <div className="hb-panel-actions" aria-hidden="true">
          <span className="hb-fake-btn hb-fake-btn--ghost"><Bookmark size={14} /> Save venue</span>
          <span className="hb-fake-btn">Add to Booking</span>
        </div>
      </article>

      <ArrowRight className="hb-flow-arrow" aria-hidden="true" />

      <article className="hb-panel">
        <header className="hb-panel-head">
          <span className="hb-panel-tab">Booking</span>
          <span className="hb-panel-sub">Pipeline</span>
        </header>
        <ol className="hb-stages">
          <li><span>Prospecting</span><em>The Grange Room</em></li>
          <li className="is-current"><span>Outreach</span><em>Millrace Hall</em></li>
          <li><span>Negotiation</span><em>Copperline Hall</em></li>
          <li><span>Booked</span><em>&mdash;</em></li>
        </ol>
      </article>

      <ArrowRight className="hb-flow-arrow" aria-hidden="true" />

      <article className="hb-panel">
        <header className="hb-panel-head">
          <span className="hb-panel-tab">Shows</span>
          <span className="hb-panel-sub">Upcoming</span>
        </header>
        <div className="hb-show">
          <div className="hb-show-date" aria-hidden="true">
            <span>MAR</span>
            <strong>14</strong>
          </div>
          <div>
            <p className="hb-venue-name">Copperline Hall</p>
            <p className="hb-venue-meta">Asheville, NC &middot; Doors 7:00 PM</p>
          </div>
        </div>
        <div className="hb-show hb-show--faint">
          <div className="hb-show-date" aria-hidden="true">
            <span>APR</span>
            <strong>02</strong>
          </div>
          <div>
            <p className="hb-venue-name">Union Depot</p>
            <p className="hb-venue-meta">Knoxville, TN</p>
          </div>
        </div>
      </article>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Request-access form (preview-safe, not connected)                  */
/* ------------------------------------------------------------------ */

function RequestAccessPreview({ id }: { id: string }) {
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "received" | "error">("idle");
  const [error, setError] = useState("");
  const inputId = `${id}-email`;
  const noteId = `${id}-note`;
  return (
    <form className="hb-form" aria-describedby={noteId} onSubmit={async (event) => {
      event.preventDefault();
      if (state === "sending" || state === "received") return;
      setState("sending"); setError("");
      const params = new URLSearchParams(window.location.search);
      const attribution = Object.fromEntries(["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"]
        .filter((key) => params.has(key)).map((key) => [key, params.get(key)!.slice(0, 160)]));
      try {
        const response = await fetch(`${BETA_API}/waitlist`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: email.trim(), attribution, website }),
          signal: AbortSignal.timeout(15000),
        });
        if (!response.ok) throw new Error(response.status === 429 ? "Too many requests. Please try again later." : "We couldn’t save your request. Please try again.");
        const data = await response.json();
        if (data.status !== "received") throw new Error("We couldn’t confirm your request. Please try again.");
        setState("received");
      } catch (reason) {
        setError(reason instanceof Error ? reason.message : "We couldn’t save your request. Please try again.");
        setState("error");
      }
    }}>
      <label htmlFor={inputId} className="hb-sr-only">Email address</label>
      <div className="hb-sr-only" aria-hidden="true">
        <label htmlFor={`${id}-website`}>Leave this field empty</label>
        <input id={`${id}-website`} tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
      </div>
      <div className="hb-form-row">
        <input id={inputId} type="email" name="email" autoComplete="email" inputMode="email" maxLength={254}
          placeholder="you@yourband.com" value={email} required disabled={state === "sending" || state === "received"}
          aria-invalid={state === "error"} onChange={(e) => { setEmail(e.target.value); setState("idle"); setError(""); }} />
        <button type="submit" className="hb-btn" disabled={state === "sending" || state === "received"}>
          {state === "sending" ? "Saving…" : state === "received" ? "Request received" : "Request beta access"}
        </button>
      </div>
      <p id={noteId} className="hb-form-note" role={state === "error" ? "alert" : "status"} aria-live="polite">
        {state === "received" ? "You’re on the waitlist. This does not create an account or grant beta access." : state === "error" ? error : "Private beta · invitation only · we’ll contact you when access is available."}
      </p>
    </form>
  );
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

const STEPS = [
  {
    icon: Users,
    title: "Choose comparable artists",
    body: "Pick a few artists at your level or a step ahead — acts who draw similar crowds in the kinds of rooms you want to play.",
  },
  {
    icon: Music,
    title: "Discover proven venues",
    body: "See the venues in their real touring history, with which of your comparables played each one. Filter by city or region and save the ones that fit.",
  },
  {
    icon: CalendarCheck,
    title: "Turn opportunities into bookings",
    body: "Add promising venues to Booking, track each pitch from first contact to confirmed, and keep booked shows in one place.",
  },
];

export default function HelloLandingBeta() {
  const [dark, toggleDark] = useLandingDarkMode();

  useEffect(() => {
    const previous = document.title;
    const icon = document.createElement("link");
    icon.rel = "icon"; icon.href = favicon;
    document.head.appendChild(icon);
    document.title = "ArtistNav — Find where artists like you are playing";
    return () => {
      document.title = previous;
      icon.remove();
    };
  }, []);

  return (
    <div className={`hb-root${dark ? " hb-dark" : ""}`}>
      <a className="hb-skip" href="#hb-main">
        Skip to content
      </a>

      {import.meta.env.DEV && <div className="hb-dev-banner" role="note">
        ArtistNav launch candidate &middot; isolated preview
      </div>}

      <header className="hb-shell hb-header">
        <a className="hb-brand" href="#top" aria-label="ArtistNav home">
          <img src={favicon} alt="" width={34} height={34} />
          <span>ArtistNav</span>
        </a>
        <nav className="hb-header-nav" aria-label="Primary">
          <a className="hb-link hb-hide-sm" href="#how-it-works">
            How it works
          </a>
          <button
            type="button"
            className="hb-icon-btn"
            onClick={toggleDark}
            aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
          >
            {dark ? <Sun size={17} /> : <Moon size={17} />}
          </button>
          <a className="hb-btn hb-btn--ghost" href={SIGN_IN_PATH}>
            Sign in
          </a>
          <a className="hb-btn hb-btn--small hb-hide-xs" href="#request-access">
            Request access
          </a>
        </nav>
      </header>

      <main id="hb-main">
        <section className="hb-shell hb-hero" id="top">
          <div className="hb-hero-copy">
            <p className="hb-eyebrow">Private beta &middot; for independent touring artists</p>
            <h1 className="hb-display">
              Find where artists like you are playing.
              <span className="hb-display-accent"> Figure out where to play next.</span>
            </h1>
            <p className="hb-lead">
              Choose artists at or just above your level. ArtistNav shows you the venues in their real touring
              history, so you can save the rooms that fit and move them into your booking pipeline.
            </p>
            <div className="hb-hero-actions">
              <a className="hb-btn" href="#request-access">
                Request beta access
              </a>
              <a className="hb-btn hb-btn--outline" href="#how-it-works">
                See how it works
              </a>
            </div>
          </div>
          <div className="hb-hero-visual">
            <NetworkDiagram />
          </div>
        </section>

        <section className="hb-section hb-section--band" aria-labelledby="hb-network-title">
          <div className="hb-shell hb-split">
            <div>
              <p className="hb-kicker">The touring network</p>
              <h2 id="hb-network-title" className="hb-h2">
                Not a venue directory. A map of where your peers actually play.
              </h2>
            </div>
            <div className="hb-prose">
              <p>
                Lists of venues tell you a room exists. They don&rsquo;t tell you whether it books music like yours.
              </p>
              <p>
                ArtistNav starts from artists you choose and works outward through the shows they&rsquo;ve played.
                When several of your comparables have played the same room, that&rsquo;s a signal worth following up.
              </p>
            </div>
          </div>
        </section>

        <section className="hb-section" id="how-it-works" aria-labelledby="hb-how-title">
          <div className="hb-shell">
            <p className="hb-kicker">How it works</p>
            <h2 id="hb-how-title" className="hb-h2">
              Three steps from &ldquo;who&rsquo;s like me?&rdquo; to a booked show
            </h2>
            <ol className="hb-steps">
              {STEPS.map((step, index) => (
                <li key={step.title} className="hb-step">
                  <span className="hb-step-num" aria-hidden="true">
                    {index + 1}
                  </span>
                  <step.icon size={20} className="hb-step-icon" aria-hidden="true" />
                  <h3>{step.title}</h3>
                  <p>{step.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="hb-section hb-section--band" aria-labelledby="hb-flow-title">
          <div className="hb-shell">
            <p className="hb-kicker">One workflow</p>
            <h2 id="hb-flow-title" className="hb-h2">
              Discover &rarr; Booking &rarr; Shows
            </h2>
            <p className="hb-section-lead">
              A venue you find in Discover can go straight into your Booking pipeline. When it&rsquo;s confirmed, it
              shows up in Shows. You do the pitching; ArtistNav keeps track of where everything stands.
            </p>
            <WorkflowPreview />
            <p className="hb-fineprint">Example data. Venue contact details aren&rsquo;t available for every venue.</p>
          </div>
        </section>

        <section className="hb-section" id="request-access" aria-labelledby="hb-cta-title">
          <div className="hb-shell">
            <div className="hb-cta">
              <p className="hb-kicker">Private beta</p>
              <h2 id="hb-cta-title" className="hb-h2">
                We&rsquo;re opening access gradually
              </h2>
              <p className="hb-section-lead">
                ArtistNav is in private beta with a small group of independent artists. Leave your email and
                we&rsquo;ll reach out when we can bring you in.
              </p>
              <RequestAccessPreview id="hb-cta-form" />
              <p className="hb-form-alt">
                Already have an account? <a href={SIGN_IN_PATH}>Sign in</a>
              </p>
            </div>
          </div>
        </section>
      </main>

      <footer className="hb-shell hb-footer">
        <div className="hb-brand hb-brand--small">
          <img src={favicon} alt="" width={22} height={22} />
          <span>ArtistNav</span>
        </div>
        <p>&copy; {new Date().getFullYear()} ArtistNav</p>
      </footer>
    </div>
  );
}
